import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, ChevronDown } from "lucide-react";
import logo from "../../assets/logo.png";
import { SearchModal } from "@/components/SearchModal";

export const SITE_NAV = [
  { to: "/nosotros", label: "El Club" },
  { to: "/noticias", label: "Noticias" },
  { to: "/historia", label: "Historia" },
  { to: "/palmares", label: "Palmarés" },
  { to: "/equipos", label: "Equipos" },
  { to: "/plantel", label: "Plantel" },
  { to: "/comision", label: "Comisión" },
  { to: "/galerias", label: "Galería" },
  { to: "/tienda", label: "Tienda" },
  { to: "/patrocinadores", label: "Sponsors" },
];

// Menú del navbar: agrupa Historia/Palmarés/Comisión bajo "El Comandante"
export const NAV_MENU = [
  { to: "/nosotros", label: "El Club" },
  { to: "/noticias", label: "Noticias" },
  {
    label: "El Comandante",
    children: [
      { to: "/historia", label: "Historia" },
      { to: "/palmares", label: "Palmarés" },
      { to: "/comision", label: "Comisión" },
    ],
  },
  { to: "/equipos", label: "Equipos" },
  { to: "/plantel", label: "Plantel" },
  { to: "/galerias", label: "Galería" },
  { to: "/tienda", label: "Tienda" },
  { to: "/patrocinadores", label: "Sponsors" },
];

export const Navbar = ({ overHero = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);
  useEffect(() => { setOpenMenu(null); }, [pathname]);

  const solid = !overHero || scrolled;
  const goSocios = () => { setOpen(false); navigate("/membresias"); };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          solid ? "bg-white/85 backdrop-blur-xl border-b border-gray-200/80 shadow-sm" : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group" aria-label="Ir al inicio">
            <img src={logo} alt="Club Comandante Leo Nowak" className="h-10 sm:h-12 w-auto transition-transform group-hover:scale-105" />
            <span className="hidden sm:flex flex-col leading-none text-left">
              <span className={`font-extrabold text-sm tracking-tight transition-colors ${solid ? "text-navy" : "text-white"}`} style={{ fontFamily: "Outfit" }}>
                COMANDANTE LEO NOWAK
              </span>
              <span className="text-[11px] font-semibold tracking-[0.18em] text-gold">CLUB · 1976—2026</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-0.5">
            {NAV_MENU.map((l) => {
              if (l.children) {
                const groupActive = l.children.some((c) => pathname.startsWith(c.to));
                const isOpen = openMenu === l.label;
                return (
                  <div
                    key={l.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(l.label)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <button
                      data-testid="nav-group-el-comandante"
                      onClick={() => setOpenMenu((v) => (v === l.label ? null : l.label))}
                      className={`relative inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                        groupActive ? (solid ? "text-navy" : "text-white") : solid ? "text-gray-600 hover:text-navy" : "text-white/80 hover:text-white"
                      }`}
                    >
                      {l.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      {groupActive && <motion.span layoutId="nav-underline" className="absolute left-3 right-6 -bottom-0.5 h-0.5 rounded-full bg-gold" />}
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.18 }}
                          className="absolute left-0 top-full pt-2 min-w-[190px]"
                        >
                          <div className="bg-white rounded-xl border border-gray-200/80 shadow-xl overflow-hidden py-1.5">
                            {l.children.map((c) => {
                              const active = pathname.startsWith(c.to);
                              return (
                                <Link
                                  key={c.to}
                                  to={c.to}
                                  data-testid={`nav-link-${c.to.slice(1)}`}
                                  className={`block px-4 py-2.5 text-sm font-semibold transition-colors ${active ? "text-navy bg-gold/10" : "text-gray-600 hover:text-navy hover:bg-gray-50"}`}
                                >
                                  {c.label}
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              const active = pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to));
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.to.slice(1)}`}
                  className={`relative px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                    active ? (solid ? "text-navy" : "text-white") : solid ? "text-gray-600 hover:text-navy" : "text-white/80 hover:text-white"
                  }`}
                >
                  {l.label}
                  {active && <motion.span layoutId="nav-underline" className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-gold" />}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-search-btn"
              onClick={() => setSearch(true)}
              aria-label="Buscar"
              className={`inline-flex items-center justify-center h-11 w-11 rounded-lg transition-colors ${solid ? "text-navy hover:bg-navy/5" : "text-white hover:bg-white/10"}`}
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              data-testid="nav-cta-desktop"
              onClick={goSocios}
              className={`hidden lg:inline-flex items-center text-sm font-semibold px-5 py-2.5 rounded-lg shadow-md transition-colors ${
                solid ? "bg-navy text-white hover:bg-[#16235a]" : "bg-gold text-navy hover:bg-[#c4940f]"
              }`}
            >
              Asociarme
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className={`lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg transition-colors ${solid ? "text-navy hover:bg-navy/5" : "text-white hover:bg-white/10"}`}
              aria-label="Abrir menú"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </nav>
      </header>

      <SearchModal open={search} onClose={() => setSearch(false)} />

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] lg:hidden bg-navy text-white flex flex-col"
          >
            <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Club" className="h-9 w-auto" />
                <span className="font-bold text-sm tracking-wide" style={{ fontFamily: "Outfit" }}>LEO NOWAK</span>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="h-11 w-11 inline-flex items-center justify-center rounded-lg hover:bg-white/10" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col justify-center px-7 gap-0.5">
              {NAV_MENU.map((l, i) => {
                if (l.children) {
                  return (
                    <motion.div key={l.label} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i + 0.08 }} className="py-2">
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-gold/80 mb-1">{l.label}</div>
                      {l.children.map((c) => {
                        const active = pathname.startsWith(c.to);
                        return (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`mobile-nav-link-${c.to.slice(1)}`}
                            onClick={() => setOpen(false)}
                            className={`block text-left text-2xl font-bold py-1.5 tracking-tight transition-colors ${active ? "text-gold" : "text-white/80"}`}
                            style={{ fontFamily: "Outfit" }}
                          >
                            {c.label}
                          </Link>
                        );
                      })}
                    </motion.div>
                  );
                }
                const active = pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to));
                return (
                  <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i + 0.08 }}>
                    <Link
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.to.slice(1)}`}
                      onClick={() => setOpen(false)}
                      className={`block text-left text-3xl font-bold py-2 tracking-tight transition-colors ${active ? "text-gold" : "text-white/90"}`}
                      style={{ fontFamily: "Outfit" }}
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
            <div className="p-7">
              <button data-testid="mobile-cta" onClick={goSocios} className="w-full bg-gold text-navy font-bold text-lg py-4 rounded-xl shadow-lg">
                Asociarme al club
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
